import React from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>
      
      <Typography variant="h3" component="h1" gutterBottom>
        Privacy Policy
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Effective Date: 8/24/25
      </Typography>
      
      <Box sx={{ mt: 4 }}>
        <Typography variant="body1" paragraph>
          Sedra ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business management software, services, and website (collectively, the "Service").
        </Typography>
        
        <Typography variant="body1" paragraph>
          By using the Service, you consent to the practices described in this Privacy Policy.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          1. INFORMATION WE COLLECT
        </Typography>
        
        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
          A. Information You Provide
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
          <li>Email address, password (hashed), and profile information</li>
          <li>Business details: company name, address, contact info</li>
          <li>Customer data: names, contact information, invoice records</li>
          <li>Payment-related data via Stripe (we do not store credit card numbers)</li>
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
          B. Information We Collect Automatically
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
          <li>Log data (IP address, browser type, pages visited, timestamps)</li>
          <li>Device information (type, OS, identifiers)</li>
          <li>Usage data (feature usage, settings, PDF generation)</li>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          2. HOW WE USE YOUR INFORMATION
        </Typography>
        <Typography variant="body1" paragraph>
          We use your information to:
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
          <li>Provide, operate, and improve the Service</li>
          <li>Authenticate and manage user accounts</li>
          <li>Enable invoice creation, customer tracking, and business functions</li>
          <li>Process payments through Stripe</li>
          <li>Send service-related emails</li>
          <li>Analyze usage trends and performance</li>
          <li>Maintain data security and prevent misuse</li>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          3. SHARING OF INFORMATION
        </Typography>
        <Typography variant="body1" paragraph>
          We do not sell your personal data. We share information only with:
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
          <li>Stripe (for payment processing)</li>
          <li>Service providers who help us operate the platform (e.g., cloud hosting)</li>
          <li>Legal or regulatory authorities when required by law</li>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          4. DATA SECURITY
        </Typography>
        <Typography variant="body1" paragraph>
          We use encryption, access controls, and other safeguards to protect your data. However, no system is completely secure. Users are responsible for keeping passwords confidential.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          5. DATA RETENTION
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your information as long as your account is active or as needed to provide the Service. You may request deletion by contacting us at admin@sedrahq.com.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          6. YOUR RIGHTS AND CHOICES
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
          <li>You may access and update your account information at any time.</li>
          <li>You may request deletion of your account and associated data.</li>
          <li>You may opt out of non-essential communications.</li>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          7. CHILDREN'S PRIVACY
        </Typography>
        <Typography variant="body1" paragraph>
          The Service is intended for users 18 and older. We do not knowingly collect data from anyone under 18.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          8. THIRD-PARTY LINKS
        </Typography>
        <Typography variant="body1" paragraph>
          Our Service may contain links to third-party websites. We are not responsible for their privacy practices.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          9. CHANGES TO THIS POLICY
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. Material changes will be communicated through the Service or via email.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          10. CONTACT US
        </Typography>
        <Typography variant="body1" paragraph>
          For questions or requests regarding your data, contact:
        </Typography>
        <Typography variant="body1" paragraph>
          admin@sedrahq.com
        </Typography>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Sedra
          </Typography>
          <Typography variant="body2" color="text.secondary">
            5 Arcadian Dr, Spring Valley, NY 10977
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default PrivacyPolicyPage; 